import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, XCircle, HelpCircle, Link2,
  Truck, Phone, ChevronRight, ChevronDown, PowerOff, Ban, RefreshCw,
} from 'lucide-react';
import { CARD } from './TechnicalUI';

// ─── LINK HEALTH ──────────────────────────────────────────────────────────────
// One panel in place of the two separate "ONUs Online" and "Users Online" numbers.
// Those are two layers of the same connection, and a technician's next action is
// decided by where they DISAGREE, not by either total:
//
//   fiber up  + session up    → Healthy          nothing to do
//   fiber up  + session down  → No session       phone fix: router / password / expiry
//   fiber down                → Fiber down       site visit: power or fiber cut
//   fiber ?   + session up    → Not on fiber map online, but no ONU mapped to them
//   fiber ?   + session down  → Offline          down, and no layer can say why
//   fiber down + session up   → Check data       contradiction: stale OLT or wrong mapping
//
// Laid out phone-first, for a field technician holding the phone in one hand: one big
// number per row, ≥56px tap rows, the whole row is the link (not a small chevron), and
// no more than three figures competing on any card. Colors are the reserved status
// palette (same greens/ambers/reds as SignalBadge), never themed, and every state carries
// an icon + word alongside its color so nothing is readable by color alone.
export const LINK_STATE_META = {
  healthy:     { label: 'Healthy',          color: '#0ca30c', icon: CheckCircle2,  hint: 'Fiber up and PPPoE session active' },
  noSession:   { label: 'No session',       color: '#fab219', icon: AlertTriangle, hint: 'ONU is online but no PPPoE session — router, password or expiry' },
  mismatch:    { label: 'Check data',       color: '#7c3aed', icon: HelpCircle,    hint: 'Traffic is flowing but the OLT reports the ONU down — stale scrape or wrong ONU mapping' },
  fiberDown:   { label: 'Fiber down',       color: '#d03b3b', icon: XCircle,       hint: 'ONU offline at the OLT — power cut or fiber break' },
  sessionOnly: { label: 'Not on fiber map', color: '#1e40af', icon: Link2,         hint: 'Online via PPPoE but no ONU is mapped to this customer' },
  unknown:     { label: 'Offline',          color: '#9ca3af', icon: HelpCircle,    hint: 'No ONU mapped and no PPPoE session' },
};

// Bar order runs best → worst → unknown so the healthy block always starts at the left
// edge and the eye reads severity left to right.
const BAR_ORDER = ['healthy', 'noSession', 'mismatch', 'fiberDown', 'sessionOnly', 'unknown'];

// The three states where the customer has no working internet right now. Deliberately NOT
// the same set as the attention rows below: 'mismatch' customers are passing traffic (so
// they count as up) but still need someone to fix the mapping, and 'sessionOnly' customers
// are up and need nothing.
const DOWN_STATES = ['noSession', 'fiberDown', 'unknown'];

// Headline verdict thresholds. An ISP always carries some routers-switched-off, so "OK" is
// a band, not 100% — these are tuned so a normal day reads green and a real event doesn't.
const VERDICT = [
  { min: 90, label: 'Network OK',   color: '#0ca30c' },
  { min: 75, label: 'Degraded',     color: '#fab219' },
  { min: 0,  label: 'Major outage', color: '#d03b3b' },
];

// "ONU offline" was one row until the OLT's own deregister reason was wired through, and it
// was the wrong row: a dying gasp means the customer's mains went out and the line will come
// back on its own, while LOS means the light stopped — that one is ours, and it needs a van.
// Lumping them together sent technicians to houses that only needed the power back on.
export const DOWN_CAUSE_META = {
  los:          { label: 'Fiber fault',      color: '#d03b3b', icon: Truck,     hint: 'Signal lost — cut, bend or pulled connector. Send someone.' },
  powerOff:     { label: 'Power off',        color: '#64748b', icon: PowerOff,  hint: "ONU sent a dying gasp — mains cut at the customer's end, usually returns by itself" },
  deactivated:  { label: 'Deactivated',      color: '#1e40af', icon: Ban,       hint: 'Switched off at the OLT on purpose — not a fault' },
  unknownCause: { label: 'Down, no reason',  color: '#9ca3af', icon: HelpCircle, hint: 'ONU is offline and the OLT reports no deregister reason' },
};

// The actionable split, in the order a technician triages: the truck job first (longest to
// fix and the only one that IS a fault), then the phone calls, then the ones to wait out,
// then the data cleanup. `cause` rows drill into the fiberDown set.
const ATTENTION_ROWS = [
  { state: 'fiberDown', cause: 'los',          icon: Truck,      label: 'Need a site visit', sub: 'Fiber signal lost — cut or bend' },
  { state: 'noSession',                        icon: Phone,      label: 'Fixable by phone',  sub: 'Router, password or expiry' },
  { state: 'fiberDown', cause: 'powerOff',     icon: PowerOff,   label: 'Power cut',         sub: 'Dying gasp — comes back with the power' },
  { state: 'fiberDown', cause: 'unknownCause', icon: HelpCircle, label: 'Down, no reason',   sub: 'ONU offline, OLT gives no cause' },
  { state: 'fiberDown', cause: 'deactivated',  icon: Ban,        label: 'Deactivated by us', sub: 'Switched off at the OLT — not a fault' },
  { state: 'mismatch',                         icon: HelpCircle, label: 'Bad data',          sub: 'Online, but the ONU mapping is wrong' },
];

// Rows carry their own count and destination: a plain state row counts the state, a cause row
// counts its slice of fiberDown. Rows with nothing in them are dropped.
function attentionRows(states, downCauses = {}) {
  return ATTENTION_ROWS
    .map(r => ({
      ...r,
      key: r.cause || r.state,
      count: r.cause ? (downCauses[r.cause] || 0) : states[r.state],
      color: (r.cause ? DOWN_CAUSE_META[r.cause] : LINK_STATE_META[r.state]).color,
      to: `/technical/connections?state=${r.state}${r.cause ? `&cause=${r.cause}` : ''}`,
    }))
    .filter(r => r.count > 0);
}

function StateBar({ states, total, dim }) {
  const shown = BAR_ORDER.filter(s => states[s] > 0);
  return (
    <div style={{ display: 'flex', height: 10, borderRadius: 5, background: 'var(--cb-eef0f3)', gap: 2, overflow: 'hidden' }}>
      {shown.map((s, i) => (
        <div
          key={s}
          title={`${states[s]} ${LINK_STATE_META[s].label}`}
          style={{
            width: `${(states[s] / total) * 100}%`,
            background: LINK_STATE_META[s].color,
            borderTopLeftRadius: i === 0 ? 5 : 0, borderBottomLeftRadius: i === 0 ? 5 : 0,
            borderTopRightRadius: i === shown.length - 1 ? 5 : 0, borderBottomRightRadius: i === shown.length - 1 ? 5 : 0,
            opacity: dim && dim !== s ? 0.3 : 1,
            transition: 'opacity .2s, width .3s',
          }}
        />
      ))}
    </div>
  );
}

// Progress ring for the headline percentage. An SVG arc rather than a bar because this one
// figure is the screen's anchor and needs to read from arm's length; the label sits in HTML
// on top of it (not <text>) so it inherits the app's font and scales with the user's text
// size settings like everything else.
function HealthRing({ pct, color, size = 104, stroke = 11 }) {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ display: 'block' }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--cl-eef0f3)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${(circumference * Math.min(100, Math.max(0, pct))) / 100} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray .4s' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 1,
      }}>
        <span style={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1, color: 'var(--c-111827)', letterSpacing: '-.02em' }}>{pct}%</span>
        <span style={{ fontSize: '.62rem', color: 'var(--c-6b7280)', lineHeight: 1.1 }}>of lines up</span>
      </div>
    </div>
  );
}

// Names the colours in the bar directly above it. Only states that actually occur get a
// column — a permanent row of zeroes trains people to stop reading the row.
function StateLegend({ states }) {
  const shown = BAR_ORDER.filter(s => states[s] > 0);
  if (shown.length === 0) return null;
  return (
    <div style={{ display: 'flex', marginTop: 14 }}>
      {shown.map((s, i) => (
        <div
          key={s}
          title={LINK_STATE_META[s].hint}
          style={{
            flex: '1 1 0', minWidth: 0, padding: '2px 6px',
            borderLeft: i === 0 ? 'none' : '1px solid var(--cb-f1f2f4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: LINK_STATE_META[s].color, flexShrink: 0 }} />
            <span style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {LINK_STATE_META[s].label}
            </span>
          </div>
          <div style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-111827)', marginTop: 2 }}>{states[s]}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Home-screen health card — the "is something wrong, and where do I look first?" screen.
 * @param {object} health    linkHealth block from /technical/summary or /technical/dashboard
 * @param {string} dateLine  subtitle under the card title (the dashboard passes today's date)
 * @param {function} [onSync]  force a fresh OLT scrape (bypasses the 2-min ONU cache) — button hidden when omitted
 * @param {boolean} [syncing]  spins the sync button and disables it while a sync is in flight
 */
// Shared shell for the "where the numbers came from" row — a bordered, tappable box rather
// than bare text, so ONUs online / PPPoE sessions up read as destinations (same weight as the
// "not linked" figure already was) instead of trailing trivia under the real content above.
const infoBoxStyle = {
  flex: '1 1 150px', minWidth: 130, padding: '10px 12px', borderRadius: 10,
  border: '1px solid var(--cb-e5e7eb)', textDecoration: 'none', color: 'inherit',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
};

export default function LinkHealthCard({ health, dateLine, onSync, syncing }) {
  // Hook must run every render regardless of the early return below (Rules of Hooks) —
  // harmless when health is null since the component bails before this state is ever read.
  const [attentionOpen, setAttentionOpen] = useState(false);
  if (!health) return null;

  const { states, downCauses, total, sessionLayerUp, fiberConfigured, sessionsUp, onu } = health;
  const down = DOWN_STATES.reduce((n, s) => n + states[s], 0);
  const up = total - down;
  const pct = total > 0 ? Math.round((up / total) * 100) : 0;
  const verdict = VERDICT.find(v => pct >= v.min);
  const rows = attentionRows(states, downCauses);
  const actionable = rows.reduce((n, r) => n + r.count, 0);

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Hero — the verdict, the ring, and the counts behind them, in that reading order. */}
      <div style={{ ...CARD, padding: '16px 16px 14px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--c-111827)', letterSpacing: '-.01em' }}>Network Overview</div>
            {dateLine && <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginTop: 2 }}>{dateLine}</div>}
          </div>
          <span style={{
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            background: `${verdict.color}17`, borderRadius: 999, padding: '6px 11px',
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: verdict.color, flexShrink: 0 }} />
            <span style={{ fontSize: '.68rem', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: verdict.color }}>
              {verdict.label}
            </span>
          </span>
          {onSync && (
            <button
              onClick={onSync}
              disabled={syncing}
              title={syncing ? 'Syncing… a full OLT scrape can take up to a minute' : 'Re-scrape the OLT now instead of waiting for the next automatic refresh (takes up to a minute)'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                width: 32, height: 32, borderRadius: 9, marginLeft: 8,
                background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', color: 'var(--text-muted)',
                cursor: syncing ? 'default' : 'pointer', opacity: syncing ? 0.6 : 1,
              }}
            >
              <RefreshCw size={15} className={syncing ? 'spin' : ''} />
            </button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <HealthRing pct={pct} color={verdict.color} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '.88rem', color: 'var(--c-374151)', marginBottom: 2 }}>
              <strong style={{ color: 'var(--c-111827)' }}>{up}</strong> of <strong style={{ color: 'var(--c-111827)' }}>{total}</strong> connections working
            </div>
            <div style={{ fontSize: '.88rem', color: 'var(--c-374151)', marginBottom: 14 }}>
              <strong style={{ color: 'var(--c-111827)' }}>{down}</strong> down
            </div>
            <StateBar states={states} total={total} />
          </div>
        </div>

        <StateLegend states={states} />
      </div>

      {/* Attention — a dropdown now: the header (icon, count, View All, chevron) is always
          there so the number is never hidden, but the tile grid itself only takes screen
          space once someone actually wants to see the triage split. */}
      {actionable > 0 ? (
        <div style={{ ...CARD, padding: '14px 6px 14px 14px', marginBottom: 12 }}>
          <div
            onClick={() => setAttentionOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 9, paddingRight: 14, marginBottom: attentionOpen ? 14 : 0, cursor: 'pointer', userSelect: 'none' }}
          >
            <AlertTriangle size={18} color="#d03b3b" style={{ flexShrink: 0 }} />
            <span style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--c-111827)' }}>{actionable} Need Attention</span>
            <Link
              to="/technical/connections"
              onClick={e => e.stopPropagation()}
              style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, fontSize: '.8rem', fontWeight: 600, flexShrink: 0 }}
            >
              View All <ChevronRight size={15} />
            </Link>
            <ChevronDown
              size={17} color="#9ca3af"
              style={{ flexShrink: 0, transform: attentionOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
            />
          </div>

          {attentionOpen && (
            <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 16 }}>
              {rows.map((r, i) => {
                const Icon = r.icon;
                return (
                  <Link
                    key={r.key}
                    to={r.to}
                    style={{
                      flex: '1 1 33%', minWidth: 104, padding: '0 12px',
                      borderLeft: i % 3 === 0 ? 'none' : '1px solid var(--cb-f1f2f4)',
                      textDecoration: 'none', color: 'inherit',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${r.color}1a`,
                      }}>
                        <Icon size={18} color={r.color} />
                      </div>
                      <span style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--c-111827)', lineHeight: 1 }}>{r.count}</span>
                    </div>
                    <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-111827)', lineHeight: 1.25 }}>{r.label}</div>
                    <div style={{ fontSize: '.68rem', color: 'var(--c-6b7280)', lineHeight: 1.3, marginTop: 2 }}>{r.sub}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div style={{
          ...CARD,
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
          border: '1px solid var(--cb-bbf7d0)', background: 'var(--cb-f0fdf4)', padding: '16px 18px',
        }}>
          <CheckCircle2 size={20} color="#0ca30c" />
          <span style={{ fontSize: '.9rem', fontWeight: 600, color: 'var(--c-166534)' }}>All clear — nothing needs a technician.</span>
        </div>
      )}

      {/* Where the numbers came from — each figure is now its own tappable box straight into
          the list it describes, instead of bare text only the third one (unmapped ONUs)
          used to get. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {fiberConfigured ? (
          <Link to="/technical/onus" style={infoBoxStyle}>
            <div>
              <div style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-111827)' }}>{onu.online} / {onu.total}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)' }}>ONUs online</div>
            </div>
            <ChevronRight size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
          </Link>
        ) : (
          <div style={infoBoxStyle}>
            <div style={{ fontSize: '.75rem', color: 'var(--c-9ca3af)' }}>No OLT configured</div>
          </div>
        )}
        {sessionLayerUp ? (
          <Link to="/technical/connections?onlineOnly=true" style={infoBoxStyle}>
            <div>
              <div style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-111827)' }}>{sessionsUp}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)' }}>PPPoE sessions up</div>
            </div>
            <ChevronRight size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
          </Link>
        ) : (
          <div style={infoBoxStyle}>
            <div style={{ fontSize: '.75rem', color: 'var(--c-9ca3af)' }}>NAS poller not running</div>
          </div>
        )}
        {fiberConfigured && onu.unmappedTotal > 0 && (
          <Link to="/technical/connections?state=sessionOnly" style={infoBoxStyle}>
            <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--c-111827)' }}>{onu.unmappedTotal} ONUs not linked to a customer</span>
            <ChevronRight size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Connections-page filter — the same six states as a thumb-height pill row. Separate from
 * the hero card on purpose: on the list page the list IS the content, so the health block
 * shrinks to just the control that filters it.
 */
export function LinkStateFilterBar({ health, activeState, activeCause, onStateChange, onCauseChange }) {
  if (!health) return null;
  const { states, downCauses, total } = health;
  const shown = BAR_ORDER.filter(s => states[s] > 0);
  const toggle = (s) => onStateChange(activeState === s ? '' : s);
  // Cause pills only appear once the list is already narrowed to Fiber down — they're a
  // second cut of that one state, and showing them next to the state pills would read as
  // six more states.
  const causes = activeState === 'fiberDown' && onCauseChange
    ? Object.keys(DOWN_CAUSE_META).filter(c => (downCauses?.[c] || 0) > 0)
    : [];

  return (
    <div style={{ margin: '14px 0 16px' }}>
      <StateBar states={states} total={total} dim={activeState} />
      {/* Scrolls sideways rather than wrapping into a wall of chips on a phone. */}
      <div style={{
        display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto', paddingBottom: 4,
        scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
      }}>
        <button
          type="button"
          onClick={() => onStateChange('')}
          style={pillStyle(!activeState, '#374151')}
        >
          <strong style={{ color: 'var(--c-111827)' }}>{total}</strong>&nbsp;All
        </button>
        {shown.map(s => {
          const meta = LINK_STATE_META[s];
          const Icon = meta.icon;
          const active = activeState === s;
          return (
            <button key={s} type="button" title={meta.hint} onClick={() => toggle(s)} style={pillStyle(active, meta.color)}>
              <Icon size={14} color={meta.color} style={{ flexShrink: 0 }} />
              <strong style={{ color: 'var(--c-111827)' }}>{states[s]}</strong>
              <span style={{ color: 'var(--c-4b5563)' }}>{meta.label}</span>
            </button>
          );
        })}
      </div>

      {causes.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', paddingBottom: 4,
          scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', alignItems: 'center',
        }}>
          <span style={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.05em', color: 'var(--c-9ca3af)', flexShrink: 0 }}>
            WHY
          </span>
          {causes.map(c => {
            const meta = DOWN_CAUSE_META[c];
            const Icon = meta.icon;
            const active = activeCause === c;
            return (
              <button
                key={c}
                type="button"
                title={meta.hint}
                onClick={() => onCauseChange(active ? '' : c)}
                style={pillStyle(active, meta.color)}
              >
                <Icon size={14} color={meta.color} style={{ flexShrink: 0 }} />
                <strong style={{ color: 'var(--c-111827)' }}>{downCauses[c]}</strong>
                <span style={{ color: 'var(--c-4b5563)' }}>{meta.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 44px minimum height — the field-service touch-target floor, not the 28px a desktop chip
// would use.
function pillStyle(active, color) {
  return {
    // `font: inherit` must come BEFORE fontSize — React applies inline style properties in
    // key order, and the `font` shorthand resets font-size, so listing it last would silently
    // undo the size below.
    font: 'inherit',
    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
    minHeight: 44, padding: '0 14px', borderRadius: 22, cursor: 'pointer',
    fontSize: '.85rem', whiteSpace: 'nowrap',
    border: `1.5px solid ${active ? color : 'var(--cb-e5e7eb)'}`,
    background: active ? `${color}14` : 'var(--cb-ffffff)',
  };
}
